/**
 * The sample viewer page (route: /project/:projectId/:sampleId).
 *
 * A full-height, space-optimised layout: the big rotated image on top — with
 * playback and the capture date overlaid on it — and a full-width
 * time-series band along the bottom. No title: the sample's name is already in
 * the sidebar. The image is height-driven and grows to fill whatever the graph
 * leaves behind.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useImages, viewerThumbnailQuery } from '@/services/image.service';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import ImageFile from './ImageFile';
import { useTranslation } from 'react-i18next';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    AlertCircleIcon,
    ImageIcon,
    PlayIcon,
    PauseIcon,
} from 'lucide-react';
import ImageGraph, { GRAPH_SERIES, seriesWithData } from './ImageGraph';
import { ViewerButton, ViewerToolbar } from './ViewerControls';
import { useSample, useSampleGraphs } from '@/services/sample.service';
import { useScanner } from '@/services/scanner.service';
import { findAcquisitionIndex, parseFocus } from '@/lib/acquisition';
import { sampleRotation } from '@/lib/rotation';
import { scannerCrop } from '@/lib/crop';
import { depthSetup } from '@/lib/depth';
import {
    FaunaAnimal,
    useSampleFaunaImageIds,
} from '@/services/fauna.service';
import FaunaPanel from './FaunaPanel';
import AcquisitionDatePicker from './AcquisitionDatePicker';
import { RootsIcon } from '@/components/icons/RootsIcon';

// Timelapse frame interval.
const PLAYBACK_INTERVAL_MS = 300;
const PREFETCH_AHEAD = 5;

// The time-series band height as a fraction of the viewport (vh) so it scales
// with screen size — the image fills whatever's left, keeping the image↔graph
// proportion roughly constant on any screen.
const GRAPH_VH = 21;

export default function ImageList() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const { projectId, sampleId, acquisitionTs } = useParams();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { data: images, isLoading, isError } = useImages(projectId, sampleId);
    const { data: sample } = useSample(projectId, sampleId);
    // Same query as the chart's (shared cache): which curves have data.
    const { data: graphs } = useSampleGraphs(projectId, sampleId);
    const graphSeries = useMemo(() => seriesWithData(graphs), [graphs]);
    const { t } = useTranslation();
    // Per-sample rotation, the same rule the sample thumbnails follow.
    const rotation = sampleRotation(sample?.rotateClockwise);
    // The black frame of the scanner the sample is bound to, cropped off in
    // the viewer. Set per scanner in the backoffice.
    const { data: scanner } = useScanner(
        projectId,
        sample?.scannerSerialNumber ?? undefined,
    );
    const crop = useMemo(() => scannerCrop(scanner), [scanner]);
    const depth = useMemo(() => depthSetup(sample, scanner), [sample, scanner]);
    // The fauna column is always there, so the image never moves: it shows
    // what the analysis found once a date of the sample has any, and until
    // then says the analysis is to come.
    const { data: faunaImageIds } = useSampleFaunaImageIds(projectId, sampleId);
    const faunaPending = !!faunaImageIds && faunaImageIds.length === 0;
    // The animal last clicked in the fauna column: the viewer zooms to it and
    // outlines it for as long as its image stays displayed.
    const [selectedAnimal, setSelectedAnimal] = useState<{
        imageId: string;
        animalId: string;
        bbox: FaunaAnimal['bbox'];
        nonce: number;
    } | null>(null);

    const safeIndex =
        images && images.length > 0
            ? Math.min(Math.max(currentIndex, 0), images.length - 1)
            : 0;

    const goTo = (newIndex: number) => setCurrentIndex(newIndex);

    /* ---- Initial position ---------------------------------------------
     * A sample opens on its latest acquisition — the one people come to look
     * at — unless the URL names another one.
     *
     * /project/:projectId/:sampleId/:acquisitionTs?x=&y= is a read-only entry
     * point for the annotation tool: it opens one acquisition, zoomed on one
     * raw-jp2 pixel. Normal navigation never writes to the URL, so this is
     * applied once per sample — the moment its image list arrives — and a
     * refetch that brings in a new acquisition does not move the viewer.
     */
    const focus = useMemo(
        () => parseFocus(searchParams.get('x'), searchParams.get('y')),
        [searchParams],
    );
    const positionedFor = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!images || images.length === 0) return;
        if (positionedFor.current === sampleId) return;
        positionedFor.current = sampleId;
        const linked = acquisitionTs
            ? findAcquisitionIndex(images, acquisitionTs)
            : -1;
        const index = linked >= 0 ? linked : images.length - 1;
        setCurrentIndex(index);
    }, [sampleId, acquisitionTs, images]);
    const handlePrevious = () => {
        setIsPlaying(false);
        goTo(Math.max(0, currentIndex - 1));
    };
    // Past the last image, back round to the first.
    const handleNext = () => {
        if (!images || images.length === 0) return;
        setIsPlaying(false);
        goTo((currentIndex + 1) % images.length);
    };
    const handleJump = (newIndex: number) => {
        setIsPlaying(false);
        goTo(newIndex);
    };
    const togglePlay = () => {
        if (!images || images.length === 0) return;
        if (isPlaying) {
            setIsPlaying(false);
            return;
        }
        if (currentIndex >= images.length - 1) goTo(0);
        setIsPlaying(true);
    };

    useEffect(() => {
        if (!isPlaying || !images || images.length === 0) return;
        const id = setInterval(() => {
            setCurrentIndex((prev) => {
                const next = prev + 1;
                if (next >= images.length) {
                    setIsPlaying(false);
                    return prev;
                }
                return next;
            });
        }, PLAYBACK_INTERVAL_MS);
        return () => clearInterval(id);
    }, [isPlaying, images]);

    // Warm the next few acquisitions. Two separate things have to be warm for
    // a step to feel instant, and only the first used to be: the presigned URL
    // (a backend round trip) and the thumbnail bytes themselves (an S3 GET the
    // browser only issues when OSD adds the layer). Kicking off an Image load
    // puts the bytes in the HTTP cache, so by the time the layer is added the
    // decode is all that's left.
    useEffect(() => {
        if (!images || images.length === 0 || !projectId || !sampleId) return;
        let cancelled = false;
        for (let i = 1; i <= PREFETCH_AHEAD; i++) {
            const idx = currentIndex + i;
            if (idx >= images.length) break;
            const img = images[idx];
            queryClient
                .fetchQuery(viewerThumbnailQuery(projectId, sampleId, img.id))
                .then((url) => {
                    if (cancelled || !url) return;
                    const preload = new Image();
                    preload.decoding = 'async';
                    preload.src = url;
                })
                .catch(() => {
                    /* a thumbnail that won't prefetch is not worth reporting;
                       the viewer retries when it actually needs it */
                });
        }
        return () => {
            cancelled = true;
        };
    }, [currentIndex, images, projectId, sampleId, queryClient]);

    // The position itself is reset by the initial-position effect above, once
    // the new sample's images are in.
    useEffect(() => {
        setIsPlaying(false);
    }, [sampleId]);

    const stateRef = useRef({ currentIndex, images, isPlaying });
    stateRef.current = { currentIndex, images, isPlaying };
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            // Typing in a field, or browsing the date picker's calendar,
            // whose arrow keys move between days.
            if (
                target &&
                (target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.closest('[role="dialog"]'))
            )
                return;
            const { currentIndex: idx, images: imgs, isPlaying: playing } =
                stateRef.current;
            if (!imgs || imgs.length === 0) return;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setIsPlaying(false);
                setCurrentIndex(Math.max(0, idx - 1));
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                setIsPlaying(false);
                setCurrentIndex((idx + 1) % imgs.length);
            } else if (e.key === ' ') {
                e.preventDefault();
                if (playing) setIsPlaying(false);
                else {
                    if (idx >= imgs.length - 1) setCurrentIndex(0);
                    setIsPlaying(true);
                }
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, []);

    // Prev / play / next and the date, floated over the bottom of the image
    // like a video player's transport — they act on the picture, so they sit
    // on it, and they come along when the viewer goes fullscreen. Play is the
    // one strong button; the date is quieter, and opens a calendar.
    const playbackBar =
        images && images.length > 0 ? (
            <ViewerToolbar className="gap-1">
                <ViewerButton
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                    aria-label={t('Previous image')}
                    title={t('Previous image')}
                >
                    <ChevronLeftIcon />
                </ViewerButton>
                <ViewerButton
                    onClick={togglePlay}
                    disabled={images.length < 2}
                    aria-label={isPlaying ? t('Pause') : t('Play timelapse')}
                    title={t('Play / pause (space)')}
                    // Filled, so a smaller disc than the arrows' hit area
                    // reads as the same size as them.
                    className="mx-0.5 h-7 w-7 bg-white text-[#1c2620] hover:bg-white/90 hover:text-[#1c2620] [&_svg]:h-3 [&_svg]:w-3 [&_svg]:fill-current"
                >
                    {isPlaying ? <PauseIcon /> : <PlayIcon className="ml-px" />}
                </ViewerButton>
                <ViewerButton
                    onClick={handleNext}
                    disabled={images.length < 2}
                    aria-label={t('Next image')}
                    title={t('Next image')}
                >
                    <ChevronRightIcon />
                </ViewerButton>
                <div className="mx-0.5 h-5 w-px bg-white/15" />
                <AcquisitionDatePicker
                    images={images}
                    currentIndex={safeIndex}
                    onSelect={handleJump}
                />
            </ViewerToolbar>
        ) : null;

    const currentImageId = images?.[safeIndex]?.id;
    const animalFocus = useMemo(
        () =>
            selectedAnimal && selectedAnimal.imageId === currentImageId
                ? { bbox: selectedAnimal.bbox, nonce: selectedAnimal.nonce }
                : null,
        [selectedAnimal, currentImageId],
    );

    const imageEl =
        images && images.length > 0 ? (
            <ImageFile
                image={images[safeIndex]}
                displayMask={false}
                playing={isPlaying}
                rotation={rotation}
                crop={crop}
                depth={depth}
                className="h-full shrink-0"
                focus={focus}
                animalFocus={animalFocus}
            >
                {playbackBar}
            </ImageFile>
        ) : null;

    // The curve legend, rendered next to the section title so the chart itself
    // doesn't need to spend vertical space on its own legend. Only the curves
    // with at least one point are named.
    const seriesLegend = (
        <div className="flex items-center gap-3">
            {GRAPH_SERIES.filter((s) => graphSeries.has(s.key)).map((s) => (
                <span
                    key={s.key}
                    className="flex items-center gap-1.5 text-[11px] text-[#5a6259]"
                >
                    <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: s.color }}
                    />
                    {t(s.label)}
                </span>
            ))}
        </div>
    );

    const graphCard = (
        <div className="bg-white border border-[#e3e5df] rounded-xl shadow-[0_1px_2px_rgba(28,38,32,0.05)] px-3 pt-2 pb-1 flex flex-col h-full">
            <div className="flex items-center justify-between gap-3 px-1 pb-1.5 border-b border-[#ecede8] mb-1.5">
                <h3 className="flex items-center gap-1.5 text-[13px] font-semibold text-[#1c2620]">
                    <RootsIcon className="h-3.5 w-3.5 text-[#2d6a4f]" />
                    {t('Roots')}
                </h3>
                {seriesLegend}
            </div>
            <div className="flex-1 min-h-0">
                <ImageGraph
                    currentIndex={safeIndex}
                    onIndexChange={handleJump}
                />
            </div>
        </div>
    );

    // The big rotated image over the wide time-series band. Playback and the
    // capture date/DPI float on the image itself. The image card is flex-1, so
    // it simply fills whatever height the graph leaves (no vh math): the image is height-driven via
    // `h-full` + the landscape aspect-ratio, and the band height stays the vh
    // value the user picks.
    const renderStudio = () => (
        <div className="flex-1 min-h-0 flex flex-col gap-2">
            {/* Image: fills the remaining height on a soft-grey stage, on the
                left, with the fauna column taking the space on its right,
                whether or not the fauna analysis exists yet. */}
            <div
                className="flex-1 min-h-0 bg-[#f4f5f1] border border-[#e3e5df] rounded-xl p-1 flex items-center justify-start gap-1 overflow-hidden"
            >
                {/* Its height from the stage, its width from its own shape,
                    the depth scale's gutter included. */}
                {imageEl}
                <FaunaPanel
                    projectId={projectId}
                    imageId={currentImageId}
                    pending={faunaPending}
                    playing={isPlaying}
                    rotation={rotation}
                    selectedAnimalId={
                        selectedAnimal?.imageId === currentImageId
                            ? selectedAnimal?.animalId
                            : undefined
                    }
                    onSelect={(animal) => {
                        if (!currentImageId) return;
                        setIsPlaying(false);
                        setSelectedAnimal((prev) => ({
                            imageId: currentImageId,
                            animalId: animal.id,
                            bbox: animal.bbox,
                            nonce: (prev?.nonce ?? 0) + 1,
                        }));
                    }}
                />
            </div>
            {/* Time-series band: full width, fixed fraction of the viewport. */}
            <div className="shrink-0" style={{ height: `${GRAPH_VH}vh` }}>
                {graphCard}
            </div>
        </div>
    );

    const emptyState = (
        <div className="text-center py-16 bg-white border border-dashed border-[#dadbd5] rounded-xl">
            <ImageIcon className="h-10 w-10 text-[#cdd0c7] mx-auto mb-3" />
            <h3 className="text-base font-semibold text-[#1c2620]">
                {t('No images')}
            </h3>
        </div>
    );

    return (
        <div className="h-full w-full max-w-[1800px] mx-auto flex flex-col p-2 gap-2">
            {isLoading && <Skeleton className="flex-1 w-full rounded-xl" />}
            {isError && (
                <Alert variant="destructive">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertTitle>{t('Error')}</AlertTitle>
                    <AlertDescription>
                        {t('Unable to load images')}
                    </AlertDescription>
                </Alert>
            )}
            {!isLoading && images && images.length === 0 && emptyState}
            {images && images.length > 0 && renderStudio()}
        </div>
    );
}
